import { type FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/app-store';
import { TIER1_COUNTRIES, BASELINE_UPDATED } from '@/config/countries';
import { truncate } from '@/utils/format';
import type { ThreatLevel } from '@/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scoreToLevel(score: number): ThreatLevel {
  if (score >= 70) return 'critical';
  if (score >= 50) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
}

const LEVEL_BAR: Record<ThreatLevel, string> = {
  critical: 'bg-red-500',
  high:     'bg-orange-500',
  medium:   'bg-yellow-500',
  low:      'bg-blue-500',
  info:     'bg-slate-500',
};

const LEVEL_TEXT: Record<ThreatLevel, string> = {
  critical: 'text-red-400',
  high:     'text-orange-400',
  medium:   'text-yellow-400',
  low:      'text-blue-400',
  info:     'text-slate-400',
};

const TREND_ICON: Record<string, string> = {
  escalating:    '↑',
  stable:        '→',
  'de-escalating': '↓',
};

const TREND_COLOR: Record<string, string> = {
  escalating:    'text-red-400',
  stable:        'text-slate-500',
  'de-escalating': 'text-emerald-400',
};

type Region = 'all' | 'asia' | 'europe' | 'mena' | 'americas' | 'africa';

const REGIONS: { id: Region; label: string }[] = [
  { id: 'all',      label: 'All' },
  { id: 'asia',     label: 'Asia' },
  { id: 'europe',   label: 'EU' },
  { id: 'mena',     label: 'MENA' },
  { id: 'americas', label: 'AMR' },
  { id: 'africa',   label: 'AFR' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RiskOverviewPanel: FC = () => {
  const { t } = useTranslation();
  const countryRisks = useAppStore((s) => s.countryRisks);
  const [region, setRegion] = useState<Region>('all');

  const sorted = useMemo(() => {
    let list = [...countryRisks].sort((a, b) => b.score - a.score);
    if (region !== 'all') {
      list = list.filter((c) => {
        const cfg = TIER1_COUNTRIES[c.code];
        return cfg?.region === region;
      });
    }
    return list;
  }, [countryRisks, region]);

  // Escalating count for header badge
  const escalatingCount = useMemo(
    () => sorted.filter((c) => c.trend === 'escalating').length,
    [sorted],
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="panel-header shrink-0">
        <span className="panel-title">{t('panels.riskOverview')}</span>
        <div className="flex items-center gap-2">
          {escalatingCount > 0 && (
            <span className="text-[9px] bg-red-900/60 text-red-400 border border-red-800/50 rounded px-1 py-0.5 font-mono">
              ↑{escalatingCount} ESC
            </span>
          )}
          <span
            className="text-[8px] text-slate-600 font-mono"
            title={`Strategic baseline last reviewed ${BASELINE_UPDATED} (monthly review cycle)`}
          >
            BASE {BASELINE_UPDATED.slice(2, 7)}
          </span>
          <span className="text-[10px] tabular-nums text-slate-500">{sorted.length}</span>
        </div>
      </div>

      {/* Region filter */}
      <div className="flex gap-0.5 px-2 py-1.5 shrink-0 border-b border-surface-border">
        {REGIONS.map((r) => (
          <button
            key={r.id}
            onClick={() => setRegion(r.id)}
            className={[
              'text-[9px] px-1.5 py-0.5 rounded font-mono transition-colors',
              region === r.id
                ? 'bg-slate-700 text-slate-200 font-semibold'
                : 'text-slate-500 hover:text-slate-400',
            ].join(' ')}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {sorted.length === 0 && (
          <p className="text-xs text-slate-500 text-center py-4">No risk data yet</p>
        )}
        {sorted.map((c, i) => {
          const level = scoreToLevel(c.score);
          return (
            <div
              key={c.code}
              className="px-2 py-2 border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors"
            >
              {/* Row 1: rank + code + name + score + trend */}
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] text-slate-700 tabular-nums w-4 shrink-0">{i + 1}</span>
                <span className="text-[9px] font-mono text-slate-500 w-6 shrink-0">{c.code}</span>
                <span className="text-[11px] text-slate-200 flex-1 truncate">{c.name}</span>
                <span className={`text-[12px] font-bold tabular-nums ${LEVEL_TEXT[level]}`}>
                  {c.score}
                </span>
                <span className={`text-[11px] font-bold w-4 text-right ${TREND_COLOR[c.trend]}`}>
                  {TREND_ICON[c.trend]}
                </span>
              </div>

              {/* Row 2: score bar + event count */}
              <div className="flex items-center gap-2 mt-1 pl-[22px]">
                <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${LEVEL_BAR[level]}`}
                    style={{ width: `${c.score}%` }}
                  />
                </div>
                {c.eventCount > 0 && (
                  <span className="text-[9px] text-slate-600 tabular-nums shrink-0">
                    {c.eventCount}ev
                  </span>
                )}
              </div>

              {/* Row 3: top threat */}
              {c.topThreats[0] && (
                <p className="text-[9px] text-slate-600 mt-0.5 pl-[22px] leading-snug">
                  {truncate(c.topThreats[0], 75)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
